import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faTrash,
  faPlus,
  faMinus,
  faArrowRight,
  faLock,
  faTruck,
  faShoppingCart,
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import '../styles/CartDrawer.css';

const FREE_SHIPPING_THRESHOLD = 35; // Standard $35 Free Shipping Threshold

export const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    getCartCount,
    getCartTotal,
  } = useCart();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const navigate = useNavigate();

  const cartTotal = getCartTotal();
  const cartCount = getCartCount();
  const amountToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);
  const shippingProgress = Math.min(100, Math.round((cartTotal / FREE_SHIPPING_THRESHOLD) * 100));

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCartOpen]);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isCartOpen) {
        closeCart();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCartOpen, closeCart]);

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    setCheckoutError('');

    try {
      const payload = {
        cartItems: cartItems.map((item) => ({
          id: item.id,
          name: item.name,
          price: item.price,
          priceNum: item.priceNum,
          squareVariationId: item.squareVariationId || `VAR_${item.id}`,
          quantity: item.quantity,
          customizations: item.selectedOptions || {},
        })),
        successUrl: `${window.location.origin}/order-success`,
        cancelUrl: `${window.location.origin}/cart`,
      };

      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        // If local dev environment without Cloudflare Pages Functions, route to checkout page
        closeCart();
        navigate('/cart');
        return;
      }

      const data = await res.json();

      if (!res.ok || !data.checkoutUrl) {
        throw new Error(data.details || data.error || 'Failed to initiate Square checkout.');
      }

      // Redirect directly to Square Hosted Checkout
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.warn('Square checkout redirect:', err.message);
      // Fallback: take user to full cart page so they can review and submit
      closeCart();
      navigate('/cart');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleGoToShop = () => {
    closeCart();
    navigate('/shop');
  };

  const handleViewCart = () => {
    closeCart();
    navigate('/cart');
  };

  if (!isCartOpen) return null;

  return (
    <div
      className="cart-drawer-overlay"
      onClick={closeCart}
      role="dialog"
      aria-modal="true"
      aria-label="Shopping Cart Drawer"
    >
      <div
        className="cart-drawer-panel"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        {/* Drawer Header */}
        <div className="cart-drawer-header">
          <div className="cart-drawer-title-wrap">
            <h2 className="cart-drawer-title">Shopping Cart</h2>
            <span className="cart-drawer-count">({cartCount})</span>
          </div>
          <button
            type="button"
            className="cart-drawer-close"
            onClick={closeCart}
            aria-label="Close cart drawer"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Free Shipping Progress Bar */}
        <div className="cart-drawer-shipping">
          <div className="cart-drawer-shipping-text">
            <FontAwesomeIcon icon={faTruck} className="cart-drawer-truck-icon" />
            {amountToFreeShipping > 0 ? (
              <span>
                Add <strong>${amountToFreeShipping.toFixed(2)}</strong> more for <strong>Free US Shipping</strong>
              </span>
            ) : (
              <span className="cart-drawer-free-unlocked">
                🎉 You've unlocked <strong>Free US Shipping</strong>!
              </span>
            )}
          </div>
          <div className="cart-drawer-progress-track">
            <div
              className={`cart-drawer-progress-fill ${amountToFreeShipping === 0 ? 'is-complete' : ''}`}
              style={{ width: `${shippingProgress}%` }}
            />
          </div>
        </div>

        {/* Cart Items List */}
        <div className="cart-drawer-items">
          {cartItems.length === 0 ? (
            <div className="cart-drawer-empty">
              <div className="cart-drawer-empty-icon">
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <h3>Your cart is empty</h3>
              <p>Looks like you haven't added any goods to your cart yet.</p>
              <button
                type="button"
                className="cart-drawer-shop-btn"
                onClick={handleGoToShop}
              >
                Explore Shop
              </button>
            </div>
          ) : (
            cartItems.map((item) => {
              const itemKey = item.cartItemId || item.id;
              return (
                <div key={itemKey} className="cart-drawer-item">
                  <Link
                    to={`/product/${item.id}`}
                    onClick={closeCart}
                    className="cart-drawer-item-thumb-link"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="cart-drawer-item-thumb"
                    />
                  </Link>

                  <div className="cart-drawer-item-info">
                    <Link
                      to={`/product/${item.id}`}
                      onClick={closeCart}
                      className="cart-drawer-item-name"
                    >
                      {item.name}
                    </Link>

                    {/* Selected Options Pills */}
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <div className="cart-drawer-item-options">
                        {item.selectedOptions.color && (
                          <span className="cart-drawer-pill">
                            Color: {item.selectedOptions.color}
                          </span>
                        )}
                        {item.selectedOptions.member && (
                          <span className="cart-drawer-pill">
                            Member: {item.selectedOptions.member}
                          </span>
                        )}
                        {item.selectedOptions.theme && (
                          <span className="cart-drawer-pill">
                            Theme: {item.selectedOptions.theme}
                          </span>
                        )}
                        {item.selectedOptions.officialAlbum && (
                          <span className="cart-drawer-pill is-album">
                            Official Album
                          </span>
                        )}
                        {item.selectedOptions.customNote && (
                          <span className="cart-drawer-note" title={item.selectedOptions.customNote}>
                            "{item.selectedOptions.customNote}"
                          </span>
                        )}
                      </div>
                    )}

                    <div className="cart-drawer-item-bottom">
                      <span className="cart-drawer-item-price">{item.price}</span>

                      {/* Quantity Controls */}
                      <div className="cart-drawer-qty-control">
                        <button
                          type="button"
                          className="cart-drawer-qty-btn"
                          onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                        <span className="cart-drawer-qty-val">{item.quantity}</span>
                        <button
                          type="button"
                          className="cart-drawer-qty-btn"
                          onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        className="cart-drawer-remove-btn"
                        onClick={() => removeFromCart(itemKey)}
                        aria-label="Remove item"
                        title="Remove from cart"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Drawer Footer with Subtotal & Checkout CTA */}
        {cartItems.length > 0 && (
          <div className="cart-drawer-footer">
            {checkoutError && (
              <div className="cart-drawer-error" role="alert">
                {checkoutError}
              </div>
            )}

            <div className="cart-drawer-summary-row">
              <span className="cart-drawer-subtotal-label">Subtotal</span>
              <span className="cart-drawer-subtotal-val">${cartTotal.toFixed(2)}</span>
            </div>

            <p className="cart-drawer-tax-note">
              Taxes and shipping calculated at checkout.
            </p>

            <button
              type="button"
              className="cart-drawer-checkout-btn"
              onClick={handleCheckout}
              disabled={isCheckingOut}
            >
              {isCheckingOut ? (
                <span>Redirecting to Square...</span>
              ) : (
                <>
                  <FontAwesomeIcon icon={faLock} />
                  <span>Checkout • ${cartTotal.toFixed(2)}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>

            <button
              type="button"
              className="cart-drawer-viewcart-btn"
              onClick={handleViewCart}
            >
              View Full Cart ({cartCount})
            </button>

            <div className="cart-drawer-trust">
              <span>🔒 Secure Square Checkout • Apple Pay, Google Pay & Cards</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
